import React from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import cssModules from 'react-css-modules'
import { Link } from 'react-router'
import { glimpseRoute } from 'lib/route_utils'
import CrowdFundingPage from 'components/crowdfunding_page'
import CrowdFundingCampaignHeader from 'components/crowdfunding_campaign_header'
import CrowdFundingTabLayout from 'components/crowdfunding_tab_layout'
import styles from './crowdfunding_detail_view.less'
import {
  Button,
  BodyText,
  Icon,
  CaptionText,
  Container,
  HeadingText,
  LoadingSpinner,
} from '@paypalcorp/pp-react'

import Strip from 'components/strip'
import FlatProgress from 'components/common/flat_progress'
import CountDown from 'components/common/count_down'
import DonateButton from 'components/pages/crowdfunding/donate_button'
import ShareCampaign from 'components/common/share_campaign_compact'
import ReportLink from 'components/pages/campaign/report_link'
import ModalAnimations from 'components/common/transitions/modal_animations'
import PpgfInset from 'components/common/ppgf_inset'
import CharityDetailsModal from 'components/common/charity_details_modal'
import { formatDate } from 'client/griffin'
import { trackClick, trackImpression } from 'lib/fpti_analytics'

import { getI18n } from 'client/i18n'

const i18n = getI18n('components/crowdfunding_detail_view')
const i18n2 = getI18n('components/donate_button')

const PageSpinner = () => {
  return (
    <div className={styles.pageLoading}>
      <LoadingSpinner screenReaderText="loading" size="xl" />
    </div>
  )
}
const CanDonateOrChipInProptypes = {
  endDate: PropTypes.string,
  canDonate: PropTypes.bool,
  canChipIn: PropTypes.bool,
}

const CanDonateOrChipIn = ({ endDate, canDonate, canChipIn }) => {
  const formattedEndDate = formatDate(endDate)

  if (!canDonate) {
    return (
      <div className={styles.donateDisabledWrapper}>
        <Icon name="goals" size="md" />
        <BodyText className={styles.donateDisabledText}>
          {i18n2('limit-reached')}
          {` ${formattedEndDate}`}
        </BodyText>
      </div>
    )
  } else if (!canChipIn) {
    return (
      <div className={styles.donateDisabledWrapper}>
        <Icon name="calendar" size="md" />
        <BodyText className={styles.donateDisabledText}>
          {i18n2('expired', { date: formattedEndDate })}
        </BodyText>
      </div>
    )
  }
  if (canChipIn) {
    return (
      <div>
        <CaptionText>{i18n('expires')}</CaptionText>
        <CountDown datetime={endDate} format={['hours', 'minutes']} />
      </div>
    )
  }
  return null
}
CanDonateOrChipIn.propTypes = CanDonateOrChipInProptypes

class CrowdfundingDetailView extends React.Component {
  static propTypes = {
    campaign: PropTypes.Object,
    campaignId: PropTypes.string,
    countryCode: PropTypes.string,
    defaultCategoryCover: PropTypes.string,
    geo: PropTypes.Object,
    glimpse: PropTypes.element,
    isEdit: PropTypes.bool,
    isLoadingDNWTransaction: PropTypes.bool,
    preview: PropTypes.bool,
    reversed: PropTypes.string,
    secondary: PropTypes.string,
    small: PropTypes.string,
  }

  constructor(props) {
    super(props)
    this.state = { showGivingFundTermsModal: false }
    this.modalRoot = document.getElementById('modal-root')
  }

  invokeGivingFundTermsModal = e => {
    e.preventDefault()
    this.setState({
      showGivingFundTermsModal: true,
    })
    this.trackFPTI('click', 'dw_giving_detail_giving_fund_info_pressed')
    this.trackFPTI('impression', 'dw_giving_paypal_fund_info_screen_shown')
  }

  onGivingFundTermsModalClose = () => {
    this.setState({
      showGivingFundTermsModal: false,
    })
    this.trackFPTI('click', 'dw_giving_paypal_fund_info_close_pressed')
  }

  trackFPTI = (eventType, event) => {
    const charity = this.props.campaign && this.props.campaign.charity
    const fptiPayload = {
      event_name: event,
      flow:
        charity && charity.name
          ? 'GENEROSITY_NETWORK_CHARITIES'
          : 'GENEROSITY_NETWORK',
      charityname: charity && charity.name,
      crid: charity && charity.nonprofit_id,
    }
    if (eventType === 'impression') {
      trackImpression(fptiPayload)
    } else {
      trackClick({
        clickName: event,
        data: fptiPayload,
      })
    }
  }

  render() {
    let {
      background_image,
      background_pan,
      currency,
      current_value = 0,
      end_date,
      goal,
      id,
      is_owner,
      pledge = 0,
      charity,
      can_chip_in: canChipIn,
      can_donate: canDonate,
    } = this.props.campaign
    const { isLoadingDNWTransaction, isEdit = false } = this.props
    let isCharity = !!charity && charity.name
    const customStyle = {
      height: '100%',
    }
    const isPreview = id === '__new_campaign__' || isEdit
    const showHeader = !isPreview

    return (
      <div>
        {isLoadingDNWTransaction && <PageSpinner />}
        {ReactDOM.createPortal(
          <ModalAnimations>{this.props.glimpse}</ModalAnimations>,
          this.modalRoot
        )}
        <CrowdFundingPage
          isPreview={isPreview}
          showHeader={showHeader}
          showFooter={!isPreview}
          whiteBg={true}
        >
          <Container styleName="campaign_container">
            <div styleName="campaignHeader">
              <CrowdFundingCampaignHeader campaignId={id} />
            </div>
            <div styleName="campaignCover">
              <Strip
                isResponsive={true}
                customStyle={customStyle}
                backgroundImage={
                  background_image || this.props.defaultCategoryCover
                }
                backgroundPan={background_pan}
              />
            </div>
            <div styleName="campaignProgress">
              <FlatProgress
                totalAmount={current_value + pledge}
                goalAmount={goal}
                showPercentage={true}
                showProgress={true}
                currencyCode={currency}
              />
              <CanDonateOrChipIn
                canChipIn={canChipIn}
                canDonate={canDonate}
                endDate={end_date}
              />
              {is_owner && !isPreview ? (
                <Button
                  as={Link}
                  to={glimpseRoute('edit', 'manage')}
                  secondary={this.props.secondary}
                  inverse={this.props.reversed}
                  small={this.props.small}
                  onClick={() =>
                    this.trackFPTI(
                      'click',
                      'dw_giving_view_fundraiser_manage_pressed'
                    )
                  }
                  styleName="manage-button"
                  data-nemo="manage"
                >
                  {i18n('manage')}
                </Button>
              ) : (
                <div styleName="donate-button">
                  <DonateButton campaignId={id} isPreview={isPreview} />
                </div>
              )}
            </div>
            <div styleName="campaignTabs">
              <CrowdFundingTabLayout campaignId={id} />
            </div>
            <div styleName="campaignSharing">
              {!isPreview && (
                <div styleName="text-center social-share-container">
                  <HeadingText size="sm">
                    {i18n('social_share_heading')}
                  </HeadingText>
                  <BodyText styleName="share-caption">
                    {i18n('social_share_subheading')}
                  </BodyText>
                  <ShareCampaign campaignId={id} />
                </div>
              )}
              <div styleName="campaignReporting">
                {!isPreview && !is_owner && <ReportLink campaignId={id} />}
              </div>
            </div>
          </Container>
          {isCharity && (
            <React.Fragment>
              <section styleName="container-center-margin-auto">
                <PpgfInset
                  cdnHost={this.props.geo.cdnHost}
                  countryCode={this.props.countryCode}
                  invokeGivingFundTermsModal={this.invokeGivingFundTermsModal}
                  isPreview={isPreview}
                />
              </section>

              {this.state.showGivingFundTermsModal && (
                <CharityDetailsModal
                  cdnHost={this.props.geo.cdnHost}
                  countryCode={this.props.countryCode}
                  onGivingFundTermsModalClose={this.onGivingFundTermsModalClose}
                  showGivingFundTermsModal={this.state.showGivingFundTermsModal}
                />
              )}
            </React.Fragment>
          )}
        </CrowdFundingPage>
      </div>
    )
  }
}

export default cssModules(CrowdfundingDetailView, styles, {
  allowMultiple: true,
})
